import { Injectable } from '@angular/core';
import { StringService } from '@app/core/string/string.service';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatService } from '@app/core/date/date-format.service';

@Injectable({
  providedIn: 'root'
})
export class DateRangeFormatService {
  constructor(
    private stringService: StringService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService
  ) {}

  /** Process multiple sorted dates to return their range, e.g. 14 Apr - 27 May 2023 */
  shortDateRangeString(dates: Date[]): string {
    if (dates.length === 0) {
      return '';
    }

    const firstDate = dates.at(0);
    const lastDate = dates.at(-1);
    const isSameYear = firstDate?.getFullYear() === lastDate?.getFullYear();
    return (
      this.dateFormatService.shortDateString(firstDate, isSameYear ? 0 : 4) +
      ' - ' +
      this.dateFormatService.shortDateString(lastDate, 4)
    );
  }

  /**
   * Process multiple dates to return their days of the week
   * @param dates to read days of the week from
   * @returns Monday-Wednesday, Friday-Saturday
   * @returns Monday, Wednesday, Friday & Weekends
   * @returns Weekdays & Saturday
   */
  daysOfWeekRangeString(dates: Date[]): string {
    const dateStrings = this.daysOfWeekStrings(dates, false);
    return this.stringService.sentenceJoin(dateStrings);
  }

  /**
   * Process multiple dates to return their abbreviated days of the week
   * @param dates to read days of the week from
   * @returns Mon-Wed, Fri-Sat
   * @returns Mon, Wed, Fri & Weekends
   * @returns Weekdays & Sat
   */
  shortDaysOfWeekRangeString(dates: Date[]): string {
    const dateStrings = this.daysOfWeekStrings(dates, true);
    return this.stringService.sentenceJoin(dateStrings);
  }

  /**
   * Produces a list of day-of-week strings from a list of dates
   * Consecutive dates are converted to ranges e.g. 'Mon-Thu'
   * All 5 weekdays get converted to 'Weekdays'
   * Both weekends get converted to 'Weekends'
   */
  daysOfWeekStrings(dates: Date[], abbreviated: boolean): string[] {
    const days = dates
      .map((d: Date) => new DateWithDayOfWeek(d))
      .reduce((uniqueDays: DateWithDayOfWeek[], current: DateWithDayOfWeek) => {
        if (!uniqueDays.map((d: DateWithDayOfWeek) => d.dayOfWeek).includes(current.dayOfWeek)) {
          uniqueDays.push(current);
        }

        return uniqueDays;
      }, [])
      .sort((a: DateWithDayOfWeek, b: DateWithDayOfWeek) => a.dayOfWeek - b.dayOfWeek);

    if (days.length == 0) {
      return [];
    }

    const monday = 0;
    const friday = 4;
    const saturday = 5;
    const sunday = 6;

    const dayWords: string[] = [];
    let range: DateWithDayOfWeek[] = [];

    days.forEach((day: DateWithDayOfWeek, i: number) => {
      // Check difference between current and the next days
      range.push(day);
      const next = days.at(i + 1);
      if (!next || next.dayOfWeek - day.dayOfWeek !== 1) {
        // If the different is !== 1, the range has ended
        if (range.length == 1) {
          dayWords.push(
            abbreviated
              ? this.dateFormatService.shortDayOfWeekString(day.date)
              : this.dateFormatService.dayOfWeekString(day.date)
          );
        } else {
          const rangeDays = range.map((d: DateWithDayOfWeek) => d.dayOfWeek);
          const includesWeekdays = rangeDays.includes(monday) && rangeDays.includes(friday);
          const includesWeekends = rangeDays.includes(saturday) && rangeDays.includes(sunday);

          if (includesWeekdays) {
            dayWords.push(this.translateService.instant('GENERIC.WEEKDAYS'));
            range = range.slice(5, 7);
          }

          if (includesWeekends) {
            range = range.slice(0, -2);
          }

          if (range.length === 1) {
            const date = range[0].date;
            dayWords.push(
              abbreviated
                ? this.dateFormatService.shortDayOfWeekString(date)
                : this.dateFormatService.dayOfWeekString(date)
            );
          } else if (range.length > 1) {
            const startDate = range.at(0)?.date;
            const endDate = range.at(-1)?.date;
            dayWords.push(
              (abbreviated
                ? this.dateFormatService.shortDayOfWeekString(startDate)
                : this.dateFormatService.dayOfWeekString(startDate)) +
                '-' +
                (abbreviated
                  ? this.dateFormatService.shortDayOfWeekString(endDate)
                  : this.dateFormatService.dayOfWeekString(endDate))
            );
          }

          if (includesWeekends) {
            dayWords.push(this.translateService.instant('GENERIC.WEEKENDS'));
          }
        }

        range = [];
      }
    });

    return dayWords;
  }
}

/**
 * Represents a date with a day-of-week starting at Monday (rather than Sunday)
 * This is to find and group weekends more easily when determining day-of-week ranges
 */
class DateWithDayOfWeek {
  public dayOfWeek: number;
  constructor(public date: Date) {
    this.dayOfWeek = (date.getDay() + 6) % 7;
  }
}
